import { useEffect, useState } from 'react'
import * as api from 'api/entityApi'
import type { FieldMask, PermissionMap } from 'types/entity'

const ALL_ALLOWED = {
  Add: true,
  Edit: true,
  View: true,
  Delete: true,
  Search: true,
  Import: true,
  Export: true,
  Download: true,
  Print: true,
  AddTask: true,
  AddAppointment: true,
  AddFollowup: true,
  AddNote: true,
  SendSms: true,
  SendMail: true,
  SendVoice: true,
  SendWhatsapp: true,
  UploadDocument: true,
  DeleteDocument: true,
  AddDeal: true,
  AddWebform: true,
  AddCustomField: true,
  AddTax: true,
  AddQuotation: true,
  AddInvoice: true,
} satisfies PermissionMap

/**
 * One request per module code, shared by every consumer — the same
 * `${userId}_${moduleCode}` caching the Sales module's usePermissions uses.
 * The legacy page called GetModuleActionList once on ready for the same reason.
 */
const permissionsCache = new Map<string, Promise<PermissionMap>>()

function loadPermissions(moduleCode?: string): Promise<PermissionMap> {
  const { userId } = api.getSessionContext()
  const key = `${userId}_${moduleCode ?? ''}`
  let promise = permissionsCache.get(key)
  if (!promise) {
    // No signal: this promise is shared, so one unmount must not cancel it for everyone.
    promise = api.fetchPermissions(moduleCode).catch((err: Error) => {
      permissionsCache.delete(key)
      console.warn('Permission lookup failed.', err)
      return ALL_ALLOWED
    })
    permissionsCache.set(key, promise)
  }
  return promise
}

/**
 * Port of `objUserAuth.GetModuleActionList`.
 *
 * The legacy version hid DOM nodes by selector (`objPermId.Edit = ".btnEditInline, #btnSave"`).
 * Here the flags are returned and components gate themselves, so a permission
 * never depends on a selector still matching. Starts permissive and tightens
 * once the API answers, matching the legacy load order.
 *
 * Pass `enabled: false` for the `EditLead` shortcut — EntityEdit.aspx.cs only
 * calls FuncGetAllPermissionByModuleCodeUserIdNew when the token does not match,
 * leaving every action enabled when it does. `moduleCode` picks the role scope
 * (lead → PAG10031, enquiry → PAG10002, same codes as the Sales module).
 */
export function usePermissions(enabled = true, moduleCode?: string): PermissionMap {
  const [permissions, setPermissions] = useState<PermissionMap>(ALL_ALLOWED)

  useEffect(() => {
    if (!enabled) {
      setPermissions(ALL_ALLOWED)
      return
    }
    let active = true
    void loadPermissions(moduleCode).then((result) => {
      if (active) setPermissions(result)
    })
    return () => {
      active = false
    }
  }, [enabled, moduleCode])

  return permissions
}

export interface FieldMasking {
  /** Applies the configured masking to a value. */
  mask: (fieldName: string, value: string) => string
  /** True when the field is configured read-only, so the edit form disables it. */
  isReadOnly: (fieldName: string) => boolean
}

/**
 * Port of `GetPageFieldMaskedList` plus the `HiddenType` handling in Kit19's
 * jsContact.js. Only "masked" and "hidden" change the value — "readonly" leaves
 * it fully visible and merely blocks editing, which is why it must not be
 * treated as a mask.
 */
export function useFieldMask(): FieldMasking {
  const [masks, setMasks] = useState<FieldMask[]>([])

  useEffect(() => {
    const controller = new AbortController()
    let active = true

    api
      .fetchFieldMasks(controller.signal)
      .then((result) => {
        if (active) setMasks(result)
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') console.warn('Field-mask lookup failed.', err)
      })

    return () => {
      active = false
      controller.abort()
    }
  }, [])

  const find = (fieldName: string) =>
    masks.find((m) => m.FieldName?.toLowerCase() === fieldName.toLowerCase())

  return {
    mask: (fieldName, value) => {
      if (!value) return value
      const hiddenType = find(fieldName)?.HiddenType
      if (hiddenType === 'hidden') return ''
      if (hiddenType === 'masked') return maskEveryOtherChar(value)
      return value
    },
    isReadOnly: (fieldName) => find(fieldName)?.HiddenType === 'readonly',
  }
}

/** Port of `funMaskFieldOddEven(str, 'even', '*')` — masks every 2nd character. */
function maskEveryOtherChar(value: string): string {
  return value
    .split('')
    .map((char, i) => ((i + 1) % 2 === 0 ? '*' : char))
    .join('')
}
